import { Box, Tooltip, Typography } from '@mui/material';

export interface PmStep {
  /** 1-basierte Nummer (1..N) — wird im Kreis angezeigt. */
  number: number;
  /** Anzeigename des Schritts, z. B. "Machbarkeit". */
  label: string;
  /** Routen-Key (data-page im Clickdummy), z. B. "machbarkeit" — wird beim Klick gefeuert. */
  key: string;
  /** 0–100 Reifegrad fuer diesen Schritt (treibt die Petrol-Saturation). */
  maturity?: number;
  /** Status-Ampel (gruen/gelb/rot/grau). */
  status?: 'done' | 'wip' | 'open' | 'idle';
}

interface PmStepNavigatorProps {
  steps: PmStep[];
  currentStepKey?: string;
  onSelect?: (step: PmStep) => void;
}

const STATUS_DOT: Record<NonNullable<PmStep['status']>, string> = {
  done: 'var(--pm-success)',
  wip: 'var(--pm-warning)',
  open: 'var(--pm-error)',
  idle: 'var(--pm-gray-300)',
};

/** Liefert den Petrol-Token passend zum Reifegrad 0..100. */
function petrolStep(maturity?: number): string {
  if (!maturity || maturity <= 0) return 'var(--pm-petrol-50)';
  // 16 Stufen zwischen 50 und 800 — mappe 0..100 -> 0..15
  const idx = Math.min(15, Math.max(0, Math.round((maturity / 100) * 15)));
  const tokens = [
    '--pm-petrol-50', '--pm-petrol-100', '--pm-petrol-150', '--pm-petrol-200',
    '--pm-petrol-250', '--pm-petrol-300', '--pm-petrol-350', '--pm-petrol-400',
    '--pm-petrol-450', '--pm-petrol-500', '--pm-petrol-550', '--pm-petrol-600',
    '--pm-petrol-650', '--pm-petrol-700', '--pm-petrol-750', '--pm-petrol-800',
  ];
  return `var(${tokens[idx]})`;
}

/**
 * PM-StepNavigator — horizontaler Pfad von 1..N Schritten mit Reifegrad-Farbskala
 * und Status-Ampel-Dot rechts oben. Im Clickdummy auf der Dashboard-Seite oben.
 */
export function PmStepNavigator({ steps, currentStepKey, onSelect }: PmStepNavigatorProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        flexWrap: 'wrap',
        padding: '12px 4px',
      }}
    >
      {steps.map((step) => {
        const active = step.key === currentStepKey;
        const dotColor = step.status ? STATUS_DOT[step.status] : null;
        return (
          <Tooltip
            key={step.key}
            title={
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  Schritt {step.number} — {step.label}
                </Typography>
                {step.maturity != null && (
                  <Typography variant="caption">Reifegrad: {step.maturity}%</Typography>
                )}
              </Box>
            }
          >
            <Box
              role="button"
              tabIndex={0}
              onClick={() => onSelect?.(step)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSelect?.(step);
                }
              }}
              sx={{
                position: 'relative',
                width: 56,
                cursor: onSelect ? 'pointer' : 'default',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.5,
                transition: 'var(--pm-transition)',
                '&:hover .step-circle': onSelect ? { transform: 'scale(1.08)' } : undefined,
              }}
            >
              <Box
                className="step-circle"
                sx={{
                  position: 'relative',
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  background: petrolStep(step.maturity),
                  border: active ? '2px solid var(--pm-petrol-800)' : '2px solid transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--pm-fg-base)',
                  fontWeight: 700,
                  fontSize: '0.85rem',
                  boxShadow: active ? '0 0 0 3px rgba(95,196,216,0.25)' : 'none',
                  transition: 'var(--pm-transition)',
                }}
              >
                {step.number}
                {dotColor && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -2,
                      right: -2,
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      background: dotColor,
                      border: '2px solid var(--pm-bg-subtle)',
                    }}
                  />
                )}
              </Box>
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.65rem',
                  color: active ? 'var(--pm-fg-base)' : 'var(--pm-fg-subtle)',
                  textAlign: 'center',
                  lineHeight: 1.1,
                  maxWidth: 56,
                  whiteSpace: 'normal',
                }}
              >
                {step.label}
              </Typography>
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
}
